import React, { useState, useEffect } from 'react';
import { FileQuestion, Plus, Edit, Trash2, Clock, CheckCircle, Settings as SettingsIcon, Eye, Shuffle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';

import { API } from '@/config';

const TestManagement = ({ token, i18n }) => {
  const [tests, setTests] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTest, setEditingTest] = useState(null);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [reshuffling, setReshuffling] = useState(false);
  // License-type filter for the admin listing: 'b_korkort' or 'taxi'.
  const [filterLicense, setFilterLicense] = useState('b_korkort');

  useEffect(() => {
    fetchTests();
    fetchQuestions();
  }, []);

  // Refetch the question pool whenever the admin switches the license/category
  // in the test-edit modal — guarantees the picker never shows the wrong pool.
  useEffect(() => {
    if (editingTest) fetchQuestions();
    // eslint-disable-next-line
  }, [editingTest?.licenseType, editingTest?.taxiCategory]);

  const fetchTests = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/admin/tests`, { headers });
      setTests(res.data || []);
    } catch (error) {
      console.error('Error fetching tests:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestions = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      // STRICT ISOLATION: only fetch questions matching the test's license type.
      // When creating/editing a Taxi test we must NEVER show B-körkort questions.
      const lic = editingTest?.licenseType || 'b_korkort';
      const taxiCat = editingTest?.taxiCategory;
      let url = `${API}/tests/questions?licenseType=${lic}`;
      if (lic === 'taxi' && taxiCat) url += `&taxiCategory=${encodeURIComponent(taxiCat)}`;
      const res = await axios.get(url, { headers });
      setQuestions(res.data || []);
    } catch (error) {
      console.error('Error fetching questions:', error);
    }
  };

  const handleReshuffleNow = async () => {
    const confirmMsg = i18n.language === 'ar'
      ? 'إعادة خلط فورية لجميع البطاقات المدفوعة (لا تشمل المجاني). كل المستخدمين سيرون أسئلة جديدة من الطلب التالي. متابعة؟'
      : 'Force an immediate re-shuffle of all paid test cards (free test is unaffected). All users will see new questions on their next request. Continue?';
    if (!window.confirm(confirmMsg)) return;
    setReshuffling(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(`${API}/admin/tests/reshuffle-now`, {}, { headers });
      toast.success(
        i18n.language === 'ar'
          ? `تمت إعادة الخلط (دورة #${res.data?.epoch || '?'})`
          : `Re-shuffle done (epoch #${res.data?.epoch || '?'})`
      );
    } catch (error) {
      console.error('Reshuffle error:', error);
      toast.error(
        i18n.language === 'ar' ? 'فشل إعادة الخلط' : 'Failed to re-shuffle'
      );
    } finally {
      setReshuffling(false);
    }
  };

  const handleCreateTest = () => {
    setEditingTest({
      title: '',
      titleAr: '',
      description: '',
      descriptionAr: '',
      category: 'all',
      // Inherit currently-filtered license so admin doesn't need to pick again.
      licenseType: filterLicense,
      duration: 30,
      passingScore: 70,
      questionsCount: 10,
      randomize: true,
      showResults: true,
      selectedQuestionIds: []
    });
    setSelectedQuestions([]);
    setModalOpen(true);
  };

  const handleEditTest = (test) => {
    setEditingTest(test);
    setSelectedQuestions(test.selectedQuestionIds || []);
    setModalOpen(true);
  };

  const handleSaveTest = async () => {
    if (!editingTest.title.trim()) {
      toast.error(i18n.language === 'ar' ? 'الرجاء إدخال عنوان الاختبار' : 'Please enter test title');
      return;
    }

    if (selectedQuestions.length === 0) {
      toast.error(i18n.language === 'ar' ? 'الرجاء اختيار أسئلة للاختبار' : 'Please select questions for the test');
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      const testData = {
        ...editingTest,
        selectedQuestionIds: selectedQuestions,
        questionsCount: selectedQuestions.length
      };

      if (editingTest.id) {
        // Update
        await axios.put(`${API}/admin/tests/${editingTest.id}`, testData, { headers });
        toast.success(i18n.language === 'ar' ? 'تم تحديث الاختبار! ✅' : 'Test updated! ✅');
      } else {
        // Create
        await axios.post(`${API}/admin/tests`, testData, { headers });
        toast.success(i18n.language === 'ar' ? 'تم إنشاء الاختبار! ✅' : 'Test created! ✅');
      }

      fetchTests();
      setModalOpen(false);
      setEditingTest(null);
      setSelectedQuestions([]);
    } catch (error) {
      console.error('Error saving test:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(
        i18n.language === 'ar' 
          ? `خطأ في حفظ الاختبار: ${errorMsg}` 
          : `Error saving test: ${errorMsg}`
      );
    }
  };

  const handleDeleteTest = async (testId) => {
    if (!window.confirm(i18n.language === 'ar' ? 'هل أنت متأكد من حذف هذا الاختبار؟' : 'Are you sure you want to delete this test?')) {
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/admin/tests/${testId}`, { headers });
      toast.success(i18n.language === 'ar' ? 'تم حذف الاختبار' : 'Test deleted');
      fetchTests();
    } catch (error) {
      console.error('Error deleting test:', error);
      toast.error(i18n.language === 'ar' ? 'خطأ في حذف الاختبار' : 'Error deleting test');
    }
  };

  const toggleQuestionSelection = (questionId) => {
    if (selectedQuestions.includes(questionId)) {
      setSelectedQuestions(selectedQuestions.filter(id => id !== questionId));
    } else {
      setSelectedQuestions([...selectedQuestions, questionId]);
    }
  };

  const filteredQuestions = (() => {
    let list = questions;
    // License-type gating: taxi tests only pull from taxi questions and vice versa.
    // Legacy questions without licenseType are treated as b_korkort.
    const tlic = editingTest?.licenseType || 'b_korkort';
    list = list.filter(q => {
      const qlic = q.licenseType || 'b_korkort';
      return qlic === tlic;
    });
    if (editingTest?.category && editingTest.category !== 'all') {
      list = list.filter(q => q.category === editingTest.category);
    }
    return list;
  })();

  // License-filtered tests for display. Legacy (no field) => b_korkort.
  const visibleTests = tests.filter(t => (t.licenseType || 'b_korkort') === filterLicense);

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-8 border border-slate-200">
        <div className="flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <FileQuestion className="w-7 h-7 text-purple-600" />
              {i18n.language === 'ar' ? 'إدارة الاختبارات' : 'Test Management'}
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              {i18n.language === 'ar' 
                ? `${tests.length} اختبار، ${questions.length} سؤال متاح`
                : `${tests.length} tests, ${questions.length} questions available`
              }
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              onClick={handleReshuffleNow}
              disabled={reshuffling}
              variant="outline"
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
              data-testid="reshuffle-now-btn"
              title={i18n.language === 'ar' ? 'إعادة خلط أسئلة البطاقات المدفوعة فوراً' : 'Force re-shuffle paid test partitions now'}
            >
              <Shuffle className={`w-4 h-4 me-2 ${reshuffling ? 'animate-spin' : ''}`} />
              {i18n.language === 'ar' ? 'إعادة الخلط الآن' : 'Re-shuffle now'}
            </Button>
            <Button
              onClick={handleCreateTest}
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
            >
              <Plus className="w-4 h-4 me-2" />
              {i18n.language === 'ar' ? 'إنشاء اختبار جديد' : 'Create New Test'}
            </Button>
          </div>
        </div>

        {/* License-type filter tabs — separates B-körkort from Taxi listings */}
        <div className="flex items-center gap-2 mb-4 border-b border-slate-200 pb-3">
          <button
            type="button"
            onClick={() => setFilterLicense('b_korkort')}
            data-testid="admin-license-tab-b-korkort"
            className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
              filterLicense === 'b_korkort'
                ? 'bg-emerald-500 text-white shadow-md'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            🚗 B-körkort ({tests.filter(t => (t.licenseType || 'b_korkort') === 'b_korkort').length})
          </button>
          <button
            type="button"
            onClick={() => setFilterLicense('taxi')}
            data-testid="admin-license-tab-taxi"
            className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
              filterLicense === 'taxi'
                ? 'bg-amber-500 text-white shadow-md'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            🚕 Taxikort ({tests.filter(t => t.licenseType === 'taxi').length})
          </button>
        </div>

        {/* Tests Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {visibleTests.map((test) => (
            <div key={test.id} className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-6 border-2 border-slate-200 hover:border-purple-300 transition-all">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h4 className="text-lg font-bold text-slate-900 mb-1">
                    {i18n.language === 'ar' ? (test.titleAr || test.title) : test.title}
                  </h4>
                  <p className="text-sm text-slate-600 line-clamp-2">
                    {i18n.language === 'ar' ? (test.descriptionAr || test.description) : test.description}
                  </p>
                </div>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600 flex items-center gap-1">
                    <FileQuestion className="w-4 h-4" />
                    {i18n.language === 'ar' ? 'الأسئلة' : 'Questions'}
                  </span>
                  <span className="font-semibold text-slate-900">{test.questionsCount}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600 flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {i18n.language === 'ar' ? 'المدة' : 'Duration'}
                  </span>
                  <span className="font-semibold text-slate-900">{test.duration} {i18n.language === 'ar' ? 'دقيقة' : 'min'}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600 flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" />
                    {i18n.language === 'ar' ? 'النجاح' : 'Passing'}
                  </span>
                  <span className="font-semibold text-slate-900">{test.passingScore}%</span>
                </div>
              </div>

              <div className="flex gap-2 pt-3 border-t border-slate-200">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleEditTest(test)}
                  className="flex-1 hover:bg-blue-50"
                >
                  <Edit className="w-4 h-4 me-1" />
                  {i18n.language === 'ar' ? 'تعديل' : 'Edit'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDeleteTest(test.id)}
                  className="flex-1 hover:bg-red-50 text-red-600"
                >
                  <Trash2 className="w-4 h-4 me-1" />
                  {i18n.language === 'ar' ? 'حذف' : 'Delete'}
                </Button>
              </div>
            </div>
          ))}
        </div>

        {visibleTests.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            <FileQuestion className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p>
              {filterLicense === 'taxi'
                ? (i18n.language === 'ar' ? 'لا توجد اختبارات تاكسي بعد. أنشئ أول اختبار!' : 'No Taxi tests yet. Create your first Taxi test!')
                : (i18n.language === 'ar' ? 'لا توجد اختبارات. أنشئ أول اختبار!' : 'No tests yet. Create your first test!')
              }
            </p>
          </div>
        )}
      </div>

      {/* Create/Edit Test Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileQuestion className="w-6 h-6 text-purple-600" />
              {editingTest?.id 
                ? (i18n.language === 'ar' ? 'تعديل الاختبار' : 'Edit Test')
                : (i18n.language === 'ar' ? 'إنشاء اختبار جديد' : 'Create New Test')
              }
            </DialogTitle>
          </DialogHeader>

          {editingTest && (
            <div className="space-y-6 mt-4">
              {/* License type badge/selector */}
              <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-lg p-3">
                <Label className="font-semibold whitespace-nowrap">
                  {i18n.language === 'ar' ? 'نوع الرخصة:' : 'License:'}
                </Label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setEditingTest({ ...editingTest, licenseType: 'b_korkort' })}
                    className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all ${
                      (editingTest.licenseType || 'b_korkort') === 'b_korkort'
                        ? 'bg-emerald-500 text-white'
                        : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                    }`}
                    data-testid="modal-license-b-korkort"
                  >
                    🚗 B-körkort
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingTest({ ...editingTest, licenseType: 'taxi' })}
                    className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all ${
                      editingTest.licenseType === 'taxi'
                        ? 'bg-amber-500 text-white'
                        : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                    }`}
                    data-testid="modal-license-taxi"
                  >
                    🚕 Taxikort
                  </button>
                </div>
                {editingTest.licenseType === 'taxi' && (
                  <span className="text-xs text-slate-500 italic ms-auto">
                    {i18n.language === 'ar' ? 'السويدية فقط — الترجمات اختيارية' : 'Swedish only — translations optional'}
                  </span>
                )}
              </div>

              {/* Taxi sub-category selector (only for Taxi tests) */}
              {editingTest.licenseType === 'taxi' && (
                <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-4">
                  <Label className="font-semibold text-amber-900 block mb-2">
                    {i18n.language === 'ar' ? 'قسم Taxikort *' : 'Taxikort-område *'}
                  </Label>
                  <p className="text-xs text-amber-800 mb-3">
                    {i18n.language === 'ar'
                      ? 'اختر القسم الذي ينتمي إليه هذا الاختبار — سيظهر للمستخدم فقط تحت القسم المختار.'
                      : 'Välj vilket område detta prov tillhör — det visas för användaren endast under det valda området.'}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {[
                      { key: 'sakerhet_och_beteende', emoji: '🛡️', title: 'Säkerhet och beteende', ar: 'الأمان والسلوك' },
                      { key: 'lagstiftning', emoji: '⚖️', title: 'Lagstiftning', ar: 'التشريعات' },
                    ].map((cat) => (
                      <button
                        key={cat.key}
                        type="button"
                        onClick={() => setEditingTest({ ...editingTest, taxiCategory: cat.key })}
                        data-testid={`modal-taxi-cat-${cat.key}`}
                        className={`p-3 rounded-lg text-sm font-semibold transition-all text-left rtl:text-right ${
                          editingTest.taxiCategory === cat.key
                            ? 'bg-amber-600 text-white shadow-md'
                            : 'bg-white border border-amber-300 text-amber-900 hover:bg-amber-100'
                        }`}
                      >
                        <div className="text-2xl mb-1">{cat.emoji}</div>
                        <div className="font-bold">{cat.title}</div>
                        <div className={`text-xs mt-0.5 ${editingTest.taxiCategory === cat.key ? 'text-amber-100' : 'text-amber-700'}`}>
                          {i18n.language === 'ar' ? cat.ar : cat.title}
                        </div>
                      </button>
                    ))}
                  </div>
                  {!editingTest.taxiCategory && (
                    <p className="text-xs text-red-600 mt-2 font-semibold">
                      ⚠️ {i18n.language === 'ar' ? 'يجب اختيار قسم قبل الحفظ' : 'Välj ett område innan du sparar'}
                    </p>
                  )}
                </div>
              )}

              {/* Basic Info */}
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>{i18n.language === 'ar' ? 'عنوان الاختبار (EN)' : 'Test Title (EN)'} *</Label>
                    <Input
                      value={editingTest.title}
                      onChange={(e) => setEditingTest({ ...editingTest, title: e.target.value })}
                      placeholder="Traffic Rules Test"
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label>{i18n.language === 'ar' ? 'عنوان الاختبار (AR)' : 'Test Title (AR)'}</Label>
                    <Input
                      value={editingTest.titleAr || ''}
                      onChange={(e) => setEditingTest({ ...editingTest, titleAr: e.target.value })}
                      placeholder="اختبار قواعد المرور"
                      className="mt-2"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>{i18n.language === 'ar' ? 'الوصف (EN)' : 'Description (EN)'}</Label>
                    <Textarea
                      value={editingTest.description || ''}
                      onChange={(e) => setEditingTest({ ...editingTest, description: e.target.value })}
                      placeholder="Test your knowledge of traffic rules"
                      className="mt-2"
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label>{i18n.language === 'ar' ? 'الوصف (AR)' : 'Description (AR)'}</Label>
                    <Textarea
                      value={editingTest.descriptionAr || ''}
                      onChange={(e) => setEditingTest({ ...editingTest, descriptionAr: e.target.value })}
                      placeholder="اختبر معرفتك بقواعد المرور"
                      className="mt-2"
                      rows={3}
                    />
                  </div>
                </div>
              </div>

              {/* Settings */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-4">
                <h5 className="font-semibold text-slate-900 flex items-center gap-2">
                  <SettingsIcon className="w-5 h-5" />
                  {i18n.language === 'ar' ? 'إعدادات الاختبار' : 'Test Settings'}
                </h5>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>{i18n.language === 'ar' ? 'الفئة' : 'Category'}</Label>
                    <Select value={editingTest.category} onValueChange={(val) => setEditingTest({ ...editingTest, category: val })}>
                      <SelectTrigger className="mt-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{i18n.language === 'ar' ? 'جميع الفئات' : 'All Categories'}</SelectItem>
                        <SelectItem value="traffic_signs">{i18n.language === 'ar' ? 'إشارات المرور' : 'Traffic Signs'}</SelectItem>
                        <SelectItem value="road_rules">{i18n.language === 'ar' ? 'قواعد الطريق' : 'Road Rules'}</SelectItem>
                        <SelectItem value="safety">{i18n.language === 'ar' ? 'السلامة' : 'Safety'}</SelectItem>
                        <SelectItem value="parking">{i18n.language === 'ar' ? 'الوقوف' : 'Parking'}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>{i18n.language === 'ar' ? 'المدة (بالدقائق)' : 'Duration (minutes)'}</Label>
                    <Input
                      type="number"
                      value={editingTest.duration}
                      onChange={(e) => setEditingTest({ ...editingTest, duration: parseInt(e.target.value) || 30 })}
                      min="5"
                      max="180"
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label>{i18n.language === 'ar' ? 'درجة النجاح (%)' : 'Passing Score (%)'}</Label>
                    <Input
                      type="number"
                      value={editingTest.passingScore}
                      onChange={(e) => setEditingTest({ ...editingTest, passingScore: parseInt(e.target.value) || 70 })}
                      min="0"
                      max="100"
                      className="mt-2"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-slate-900">{i18n.language === 'ar' ? 'عشوائية الأسئلة' : 'Randomize Questions'}</div>
                      <div className="text-sm text-slate-500">{i18n.language === 'ar' ? 'ترتيب عشوائي للأسئلة' : 'Shuffle questions order'}</div>
                    </div>
                    <Switch
                      checked={editingTest.randomize}
                      onCheckedChange={(val) => setEditingTest({ ...editingTest, randomize: val })}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-slate-900">{i18n.language === 'ar' ? 'إظهار النتائج' : 'Show Results'}</div>
                      <div className="text-sm text-slate-500">{i18n.language === 'ar' ? 'إظهار النتيجة بعد الاختبار' : 'Show score after test'}</div>
                    </div>
                    <Switch
                      checked={editingTest.showResults}
                      onCheckedChange={(val) => setEditingTest({ ...editingTest, showResults: val })}
                    />
                  </div>
                </div>
              </div>

              {/* Question Selection */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h5 className="font-semibold text-slate-900">
                    {i18n.language === 'ar' ? 'اختيار الأسئلة' : 'Select Questions'} ({selectedQuestions.length}/{filteredQuestions.length})
                  </h5>
                  {filteredQuestions.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (selectedQuestions.length === filteredQuestions.length) {
                          setSelectedQuestions([]);
                        } else {
                          setSelectedQuestions(filteredQuestions.map(q => q.id));
                        }
                      }}
                    >
                      {selectedQuestions.length === filteredQuestions.length 
                        ? (i18n.language === 'ar' ? 'إلغاء الكل' : 'Deselect All')
                        : (i18n.language === 'ar' ? 'تحديد الكل' : 'Select All')
                      }
                    </Button>
                  )}
                </div>

                <div className="border border-slate-200 rounded-lg max-h-96 overflow-y-auto">
                  {filteredQuestions.length > 0 ? (
                    <div className="divide-y divide-slate-200">
                      {filteredQuestions.map((question) => (
                        <div
                          key={question.id}
                          className="p-3 hover:bg-slate-50 cursor-pointer"
                          onClick={() => toggleQuestionSelection(question.id)}
                        >
                          <div className="flex items-start gap-3">
                            <Checkbox
                              checked={selectedQuestions.includes(question.id)}
                              onCheckedChange={() => toggleQuestionSelection(question.id)}
                            />
                            <div className="flex-1">
                              <div className="text-sm text-slate-900">
                                {question.question?.[i18n.language] || question.question?.en || 'N/A'}
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge className="bg-blue-100 text-blue-700 text-xs">
                                  {question.category}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <FileQuestion className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                      <p className="text-sm">
                        {i18n.language === 'ar' 
                          ? 'لا توجد أسئلة في هذه الفئة. أضف أسئلة أولاً في قسم إدارة المحتوى.'
                          : 'No questions in this category. Add questions first in Content Management.'
                        }
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              {i18n.language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleSaveTest}
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
            >
              {i18n.language === 'ar' ? 'حفظ الاختبار' : 'Save Test'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TestManagement;
